// Paleta por CONCEPTO, no por posición.
//
// Este archivo existe por un hallazgo concreto: en la pantalla de Estadísticas
// actual, `#34c38f` significa cinco cosas distintas en cinco lugares —"Ingresos",
// "tu mejor día", "Tickets por estadía", "fin de semana" y "período actual"—
// porque cada gráfico saca el color de la POSICIÓN en su array de series.
//
// Con eso, prender leyendas no arregla nada: hay gráficos con la leyenda ya
// visible donde igual aparecen dos cuadraditos del mismo color con nombres
// distintos (5 colores declarados para 6 series).
//
// La regla es una sola:
//
//     El color se pide por concepto. Nunca se toma del índice del array.
//
// Y hay cuatro bancos que NO se mezclan jamás en un mismo gráfico:
//
//   IDENTIDAD  ¿qué es este pedazo?      origen del dinero, modalidad
//   TIEMPO     ¿esto es ahora o antes?   comparaciones contra el período previo
//   POLARIDAD  ¿entra o sale plata?      ingresos contra egresos
//   ESTADO     ¿esto está mal?           anulados, alertas, día incompleto
//
// La paleta actual del Monitor se validó contra contraste y daltonismo y falla:
// un color lee como gris (croma 0.033), otro se sale de la banda de luminosidad,
// y el peor par queda a ΔE 4.5 bajo protanopía —o sea, indistinguible—. Los
// colores de acá abajo pasaron esas tres pruebas.

// ── IDENTIDAD · de dónde viene la plata ────────────────────────────────
//
// Cuatro y no siete a propósito. Con siete orígenes no existe paleta que
// aguante: al filtrarse las series vacías cambian las adyacencias, y siempre
// aparece un par por debajo del umbral de daltonismo. Agrupar es la decisión de
// diseño, no una limitación de la herramienta.
export const ORIGEN = {
  tickets:  { color: "#2a78d6", label: "Tickets" },
  abonados: { color: "#eda100", label: "Abonados" },
  convenios:{ color: "#e87ba4", label: "Convenios" },
  otros:    { color: "#4a3aa7", label: "Otros ingresos" },
};

// ── SUB-IDENTIDAD · modalidad del ticket ───────────────────────────────
//
// Las cuatro modalidades son subdivisiones de "Tickets", así que NO llevan
// tonos distintos: llevan la rampa del azul de Tickets, ordenada por duración.
// Que sean el mismo tono comunica que son la misma familia; que el orden siga a
// la duración hace que la rampa se lea sola.
export const MODALIDAD = {
  hour:  { color: "#86b6ef", label: "Por hora" },
  stay:  { color: "#3987e5", label: "Por estadía" },
  tn:    { color: "#1c5cab", label: "Por turno" },
  plana: { color: "#0d366b", label: "Tarifa plana" },
};

// ── INTENSIDAD · el mismo concepto, más fuerte o más flojo ─────────────
//
// No es un banco nuevo: es la familia de Tickets graduada. Aparece cuando un
// gráfico marca "el día más movido" y "el más flojo" entre barras que son todas
// lo mismo. Antes eso se pintaba verde y rojo, que es la lectura de POLARIDAD
// —entra plata / sale plata— aplicada a algo que no tiene signo: un sábado
// flojo no es una pérdida. Graduar el mismo tono dice "más" y "menos" sin
// inventar un juicio, y sobrevive impreso en blanco y negro.
export const INTENSIDAD = {
  flojo:  MODALIDAD.hour.color,
  normal: ORIGEN.tickets.color,
  fuerte: MODALIDAD.plana.color,
};

// ── LIBRE · conceptos que nombra el cliente ────────────────────────────
//
// Las categorías de vehículo ("Auto", "Moto", "Camioneta", "Combi") y las
// formas de pago ("Efectivo", "Cash", "Tarjeta Naranja") las escribe cada
// cliente en su idioma. No hay concepto fijo que proteger, así que acá SÍ hace
// falta una paleta: lo que no puede pasar es que salga del índice del array.
//
// Son los tonos de identidad más cuatro derivados del mismo criterio, y no una
// segunda gama: dos familias de color conviviendo en la misma pantalla es
// exactamente lo que este archivo vino a terminar. Un gráfico de LIBRE nunca
// dibuja series de ORIGEN al mismo tiempo, así que el azul de acá no le puede
// robar la identidad a "Tickets" adentro de un mismo gráfico.
//
// El orden no es decorativo: está armado para que los primeros —los que le
// tocan a las categorías más frecuentes— caigan lo más lejos posible entre sí.
// Medido con CIEDE2000 sobre los ocho: el peor par queda en 9,6 con protanopía,
// y el peor par de tonos contiguos en 34,3. La paleta de identidad, que ya está
// validada, tiene su peor par en 10,1.
export const LIBRE = [
  "#0f7f5c",              // verde azulado
  ORIGEN.abonados.color,  // ámbar
  ORIGEN.otros.color,     // violeta
  ORIGEN.convenios.color, // rosa
  ORIGEN.tickets.color,   // azul
  "#c2410c",              // ladrillo
  "#a01e6e",              // magenta
  MODALIDAD.hour.color,   // celeste
];

/**
 * Índice estable a partir del nombre. La misma categoría recibe siempre el
 * mismo color aunque cambie de puesto al cambiar el período: si el color
 * saliera de la posición, "Moto" pasaría de tercera a segunda al mover el
 * filtro y el dueño creería que está mirando otra cosa.
 */
function indiceEstable(nombre) {
  const s = String(nombre || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * La paleta de UNA lista de nombres. Devuelve la función que le da color a cada
 * uno.
 *
 * Se pide de a listas y no de a un nombre por vez porque el nombre solo no
 * alcanza: el hash puede mandar dos nombres distintos al mismo casillero y ahí
 * quedan dos porciones del MISMO color en la misma torta, que es peor que
 * cualquier paleta fea. No es hipotético —medido sobre listas reales,
 * "Efectivo" y "Tarjeta" chocan, y "Camioneta" y "Camión" también—. Viendo la
 * lista completa, el que llega segundo se corre al casillero libre siguiente.
 *
 * El reparto se hace en orden alfabético y no en el orden en que vienen: así
 * depende del CONJUNTO de nombres y no de cuál vendió más, que es lo que cambia
 * al mover el filtro de período. Mientras el cliente no agregue ni borre una
 * categoría, los colores no se mueven.
 *
 * Con más de ocho nombres se repite algún color: no hay noveno color, y sumar
 * uno más sería sumar un tono que ya no se distingue de otro.
 */
export function paletaLibre(nombres) {
  const ocupado = new Array(LIBRE.length).fill(false);
  const asignado = {};
  const unicos = Array.from(new Set((nombres || []).map((n) => String(n ?? ""))));
  unicos.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

  unicos.forEach((nombre) => {
    let i = indiceEstable(nombre) % LIBRE.length;
    for (let paso = 0; paso < LIBRE.length && ocupado[i]; paso++) {
      i = (i + 1) % LIBRE.length;
    }
    ocupado[i] = true;
    asignado[nombre] = LIBRE[i];
  });

  return (nombre) => {
    const clave = String(nombre ?? "");
    return asignado[clave] || LIBRE[indiceEstable(clave) % LIBRE.length];
  };
}

// ── TIEMPO · ahora contra antes ────────────────────────────────────────
//
// El pasado nunca lleva color. Es gris punteado y va detrás.
// Sin esta regla, "período actual" termina siendo verde en un gráfico y azul en
// el de al lado, pegados.
export const TIEMPO = {
  actual:   { color: null, label: "Este período" },   // usa el color de identidad
  anterior: { color: "#b0afa9", label: "Período anterior", dash: 4, fill: false },
};

// ── POLARIDAD · entra o sale ───────────────────────────────────────────
//
// Verde contra rojo da ΔE 4.1 bajo deuteranopía: no distingue nada para el 8%
// de los hombres. Lo que separa las series es el SIGNO y la DIRECCIÓN —los
// egresos se dibujan debajo del cero—, no el color. Un gráfico así se entiende
// impreso en blanco y negro.
export const POLARIDAD = {
  entra: { color: "#0ca30c", label: "Entra", signo: "+" },
  sale:  { color: "#d03b3b", label: "Sale", signo: "−", bajoElCero: true },
};

// ── ESTADO · algo para mirar ───────────────────────────────────────────
//
// Nunca como serie suelta dentro de un gráfico de identidad: `#fab219` contra el
// ámbar de Abonados da ΔE 4.8, son el mismo color a los ojos. Estado va siempre
// con ícono y rótulo, y en su propio bloque.
export const ESTADO = {
  anulado: { color: "#fab219", label: "Anulado" },
  alerta:  { color: "#d03b3b", label: "Alerta" },
};

// El día en curso no tiene color propio: es el color de su serie, rayado.
// Así se lee "esto todavía no terminó" sin inventar un quinto significado.
export const EN_CURSO = { pattern: "slantedLines", opacity: 0.55, angle: 45 };

/**
 * ANCHO MÁXIMO DE LECTURA.
 *
 * Las tarjetas ocupan las 12 columnas —el fondo de lado a lado se ve bien— pero
 * el TEXTO no puede. En una pantalla de 2.200 px una frase queda de 150
 * caracteres en un solo renglón, y al volver de la derecha el ojo no encuentra
 * dónde empieza la siguiente. Lo cómodo está entre 60 y 90 caracteres.
 *
 * Se aplica al texto que se LEE —frases, matices, los renglones del resumen—,
 * nunca a la tarjeta ni a los gráficos, que sí aprovechan todo el ancho.
 */
export const ANCHO_LECTURA = 880;

// ── Neutros de la pantalla ─────────────────────────────────────────────
export const UI = {
  texto:      "#1f2937",
  textoSuave: "#6b7280",
  linea:      "#e5e7eb",
  fondo:      "#ffffff",
  fondoSuave: "#f9fafb",
};

/**
 * El color de un concepto. Si el concepto no está en el diccionario devuelve un
 * gris, nunca un color de otro banco: es preferible que algo se vea sin
 * identificar a que se vea con la identidad de otra cosa.
 */
export function colorDe(banco, clave) {
  const dic = { origen: ORIGEN, modalidad: MODALIDAD, estado: ESTADO, polaridad: POLARIDAD }[banco];
  return (dic && dic[clave] && dic[clave].color) || "#b0afa9";
}

export function etiquetaDe(banco, clave) {
  const dic = { origen: ORIGEN, modalidad: MODALIDAD, estado: ESTADO, polaridad: POLARIDAD }[banco];
  return (dic && dic[clave] && dic[clave].label) || clave;
}

/**
 * Serie de ApexCharts a partir de conceptos, para no volver a escribir
 * `colors: [...]` en cada gráfico y que se desincronice del orden de las series.
 */
export function seriesPorConcepto(banco, items) {
  return {
    series: items.map((i) => ({ name: etiquetaDe(banco, i.clave), data: i.data })),
    colors: items.map((i) => colorDe(banco, i.clave)),
  };
}
